import { gql } from '@vtex/graphql-utils'
import { graphql } from 'gatsby'
import { mark } from 'src/sdk/tests/mark'
import type { PageProps } from 'gatsby'
import { slugify } from 'src/utils/slugfy'
import { isBrandSlug, isSportSlug } from 'src/utils/cmsNameSlug'
import { getCMSPageDataByContentType } from 'src/cms/client'
import { CollectionPageTemplate } from 'src/components/collection/CollectionPageTemplate'
import { CategoryPageTemplate } from 'src/components/category/CategoryPageTemplate'
import { BreadcrumbJsonLd, GatsbySeo } from 'gatsby-plugin-next-seo'
import type {
  CollectionPageQueryQuery,
  CollectionPageQueryQueryVariables,
  ServerCollectionPageQueryQuery,
} from '@generated/graphql'
import { currentBreadcrumb } from 'src/components/ui/Breadcrumb/Breadcrumb'
import getCollectionServerSideData from 'src/utils/plp/getCollectionServerSideData'
import getCategoryServerSideData from 'src/utils/plp/getCategoryServerSideData'
import getPlacementsServerData from 'src/utils/placements/getPlacementsServerData'
import { ProductStripe } from 'src/components/global/ProductStripe'
import { getProductStripeData } from 'src/cms/hooks/getProductStripeData'
import { DiscountStripeProvider } from 'src/components/contexts/DiscountStripeContext'
import { handleSSRError } from 'src/utils/handleSSRError'
import { ItemListJsonLd } from 'src/components/restructure/product/sections/ItemListJsonLd/ItemListJsonLd'
import { ITEMS_PER_PAGE } from 'src/constants'
import { getContextualFiltersServerData } from 'src/utils/contextualFilters/getContextualFiltersServerData'

import { axiosClient } from '../services/axiosInstance'

export type Props = PageProps<
  CollectionPageQueryQuery,
  CollectionPageQueryQueryVariables,
  unknown,
  ServerCollectionPageQueryQuery
> & { slug: string; serverData: any }

function Page(props: Props) {
  if (!props || !props.serverData) {
    return null
  }

  const {
    serverData,
    data: { site },
    location: { pathname, search },
  } = props

  const { seo, data, stripeData, searchParams, searchResult } = serverData

  const breadcrumb = currentBreadcrumb(
    data.collection?.breadcrumbList.itemListElement
  )

  const substituteTitle = breadcrumb
    ?.map(
      (item: { label: string }, idx: number) =>
        `${idx === 0 ? '' : ' -'} ${item.label}`
    )
    .join('')
    .concat(' | Decathlon')

  const title = seo?.title || substituteTitle || site?.siteMetadata?.description

  const substituteDescription = `${title}, a Decathlon online é a maior loja de esportes com produtos fitness, de musculação, ciclismo, futebol, corrida, camping, trilha, natação e mais. Confira!`
  const description =
    seo?.metaTagDescription ||
    substituteDescription ||
    site?.siteMetadata?.description

  return (
    <>
      {/* SEO */}
      <GatsbySeo
        title={title}
        description={description}
        canonical={`${site?.siteMetadata?.siteUrl}${pathname}${search}`}
        language="pt-br"
        openGraph={{
          images: [{ url: site?.siteMetadata?.ogImage ?? '' }],
          type: 'website',
          title,
          description,
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={data.collection?.breadcrumbList.itemListElement ?? []}
      />
      <ItemListJsonLd
        products={searchResult?.products}
        siteUrl={site?.siteMetadata?.siteUrl}
        pageUrl={`${pathname}${search}`}
        name={title}
        description={description}
        currentPage={searchParams?.page}
        itemsPerPage={ITEMS_PER_PAGE}
      />
      <DiscountStripeProvider stripeData={stripeData}>
        <div className="w-full hidden sm:block">
          <ProductStripe />
        </div>

        {serverData?.pageType === 'collection' ? (
          <CollectionPageTemplate
            {...props}
            type="página de lista de produto"
          />
        ) : (
          <CategoryPageTemplate
            {...props}
            title={title}
            type="página de lista de produto"
          />
        )}
      </DiscountStripeProvider>
    </>
  )
}

/**
 * This query is run during SSG
 * */
export const querySSG = graphql`
  query CollectionPageQuery {
    site {
      ...SiteInformation
    }
  }
`

export const querySSR = gql`
  query ServerCollectionPageQuery($slug: String!) {
    collection(slug: $slug) {
      seo {
        title
        description
      }
      breadcrumbList {
        itemListElement {
          item
          name
          position
        }
      }
      meta {
        selectedFacets {
          key
          value
        }
      }
    }
  }
`

export const getServerData = async ({
  params: { slug },
  query,
}: {
  params: Record<string, string>
  query: Record<string, string> | undefined
}) => {
  const ONE_YEAR_CACHE = `s-maxage=31536000, stale-while-revalidate`
  const PLP_CONTEXTUAL_FILTERS_CACHE = `max-age=300, s-maxage=7200, stale-while-revalidate`
  let currentStep = 'initializing'

  try {
    // Blog posts are handled by src/pages/blog/[...slug].tsx
    if (
      typeof slug === 'string' &&
      slug.replace(/^\/+/, '').startsWith('blog/')
    ) {
      return {
        status: 404,
        props: {},
      }
    }

    const { execute } = await import('src/server/index')
    const newSlug = slugify(slug)

    currentStep = 'getProductStripeData'
    const stripeData = await getProductStripeData({
      CMS_VERSION_ID: query?.versionId ?? '',
    })

    currentStep = 'getCMSPageDataByContentType:collection-page'
    const collectionPage = (await getCMSPageDataByContentType({
      contentType: 'collection-page',
      params: {
        filters: { name: 'Coleções - URLs Amigavéis (cuidado ao editar)' },
      },
    })) as {
      sections: Array<{
        name: string
        data: {
          collections: Array<{
            newURL: string
            originalURL: string
            metatag: { title: string; description: string }
          }>
        }
      }>
    } | null

    const urls = collectionPage?.sections.find(
      (section) => section.name === 'Páginas de coleção com URL amigável'
    )

    const friendlyURLs = urls?.data.collections

    const collectionPageFromCms = friendlyURLs?.find(
      (url) => url.newURL === `/${slug}`
    )

    if (collectionPageFromCms) {
      currentStep = 'getCollectionServerSideData'
      const [
        {
          searchParams: collectionSearchParams,
          searchResult: collectionSearchResult,
        },
        contextualFiltersData,
      ] = await Promise.all([
        getCollectionServerSideData({
          originalUrl: collectionPageFromCms?.originalURL ?? '',
          query,
          slug,
        }),
        getContextualFiltersServerData(),
      ])

      return {
        status: 200,
        props: {
          stripeData,
          pageType: 'collection',
          data: collectionPageFromCms,
          seo: {
            title: collectionPageFromCms.metatag.title,
            description: collectionPageFromCms.metatag.description,
          },
          searchResult: collectionSearchResult,
          searchParams: collectionSearchParams,
          contextualFiltersData,
        },
        headers: {
          'cache-control': PLP_CONTEXTUAL_FILTERS_CACHE,
        },
      }
    }

    const physicalProduct = newSlug.split('/')

    if (physicalProduct.length === 3 && physicalProduct[0] === 'product') {
      const url = `/pesquisa?q=${physicalProduct[1]}&utm_source=loja-fisica&utm_medium=qr-code-balisagem&utm_campaign=loja-${physicalProduct[2]} `

      return {
        status: 301,
        props: {},
        headers: {
          'cache-control': ONE_YEAR_CACHE,
          location: url,
        },
      }
    }

    if (isBrandSlug(newSlug)) {
      currentStep = 'getCMSPageDataByContentType:marcas'
      const cmsDataBrands = await getCMSPageDataByContentType({
        contentType: 'marcas',
        params: {
          filters: { name: `${newSlug}` },
        },
      })

      if (cmsDataBrands !== null) {
        const url = `/marcas/${newSlug}`

        return {
          status: 301,
          props: null,
          headers: {
            'cache-control': ONE_YEAR_CACHE,
            location: url,
          },
        }
      }
    }

    if (isSportSlug(newSlug)) {
      currentStep = 'getCMSPageDataByContentType:esportes'
      const cmsDataSports = await getCMSPageDataByContentType({
        contentType: 'esportes',
        params: {
          filters: { name: `${newSlug}` },
        },
      })

      if (cmsDataSports !== null) {
        const url = `/esportes/${newSlug}`

        return {
          status: 301,
          props: null,
          headers: {
            'cache-control': ONE_YEAR_CACHE,
            location: url,
          },
        }
      }
    }

    currentStep = 'graphqlExecuteQuerySSR'
    const { data } = await execute({
      operationName: querySSR,
      variables: { slug: newSlug },
    })

    if (data === null && !collectionPageFromCms) {
      return {
        status: 301,
        props: null,
        headers: {
          'cache-control': ONE_YEAR_CACHE,
          location: `/pesquisa/?q=${encodeURIComponent(
            newSlug.replaceAll('/', ' ')
          )}`,
        },
      }
    }

    if (slug !== newSlug) {
      return {
        status: 301,
        props: null,
        headers: {
          'cache-control': ONE_YEAR_CACHE,
          location: `/${newSlug}`,
        },
      }
    }

    const { data: seo } = await axiosClient.get(
      `https://decathlonstore.myvtex.com/api/catalog_system/pub/portal/pagetype/${newSlug}`
    )

    currentStep = 'getCategoryServerSideData'
    const categoryName = slug.replaceAll('/', ' > ')

    const [
      {
        searchParams: categorySearchParams,
        searchResult: categorySearchResult,
      },
      placementData,
      contextualFiltersData,
    ] = await Promise.all([
      getCategoryServerSideData({
        slug,
        query,
        breadCrumbList: data?.collection.breadcrumbList,
      }),
      getPlacementsServerData({
        categoryName,
      }),
      getContextualFiltersServerData(),
    ])

    let cmsData = null

    try {
      currentStep = 'getCMSPageDataByContentType:category-plp'
      cmsData = await getCMSPageDataByContentType({
        contentType: 'category-plp',
        params: {
          filters: { name: newSlug },
        },
        pageId: query?.categoryPageId || undefined,
        versionId: query?.categoryVersionId || undefined,
      })
    } catch {
      cmsData = null
    }

    return {
      status: 200,
      props: {
        stripeData,
        pageType: 'category',
        data,
        seo,
        searchParams: categorySearchParams,
        searchResult: categorySearchResult,
        placementData: { ...placementData, categoryName },
        cmsData,
        contextualFiltersData,
      },
      headers: {
        'cache-control': PLP_CONTEXTUAL_FILTERS_CACHE,
      },
    }
  } catch (err) {
    handleSSRError({
      err,
      query,
      slug,
      stepName: currentStep,
    })

    return {
      status: 500,
      props: {},
      headers: {
        'cache-control': 's-maxage=31536000, stale-while-revalidate',
      },
    }
  }
}

Page.displayName = 'Page'
export default mark(Page)
